<script>
  import { mapGetters, mapState } from 'vuex';
  import 'firebase/auth';
  import rules from '@/helpers/validation-rules';
  import EventBus from '@/plugins/eventBus';
  import { AUTH_PROVIDERS, SIGN_UP_STEPS } from '@/helpers/enums';
  import ReCaptcha from '@/components/form/ReCaptcha.vue';
  import OutlinedInput from '@/components/form/OutlinedInput.vue';
  import IconGoogle from '@/assets/images/logos/005-google.svg';
  import IconAlert from '@/assets/images/svg/icons/icon-alert.svg';
  import auth from '@/mixins/auth';

  const ERROR_SHOW_TIME_MS = 2500;

  export default {
    components: {
      IconGoogle,
      IconAlert,
      OutlinedInput,
      ReCaptcha,
    },

    mixins: [auth],

    props: {
      value: Boolean,
    },

    data: () => ({
      rules,
      AUTH_PROVIDERS,
      isLoading: false,
      socialIsPending: false,

      model: {
        email: '',
        password: '',
        recoverEmail: '',
        displayName: '',
      },

      mode: 'test',
      updateKey: false,

      resetPasswordFormValid: false,

      formValid: false,
      showPassword: false,
      forgotPassword: false,
      showSignUpForm: true,

      hasSocialError: false,
      errorTimerId: null,
    }),

    computed: {
      ...mapState(['loginError', 'user']),
      ...mapGetters(['isUserSuspended', 'isUserBanned']),
    },

    watch: {
      user: {
        deep: true,
        handler(val, oldVal) {
          if (oldVal === '' && typeof val === 'object') {
            this.$store.dispatch('changeLoginDialogState', false);
          }
        },
      },

      show() {
        this.forgotPassword = false;
      },
      loginError: {
        deep: true,
        handler(val) {
          if (!val || this.isUserBanned) return;

          this.$store.dispatch('showAlertDialog', {
            title: 'Error',
            text: val,
          });
        },
      },
    },

    methods: {
      showSocialError() {
        clearTimeout(this.errorTimerId);

        this.hasSocialError = true;

        this.errorTimerId = setTimeout(this.hideSocialError, ERROR_SHOW_TIME_MS);
      },

      hideSocialError() {
        clearTimeout(this.errorTimerId);
        this.hasSocialError = false;
      },

      async loginSubmit() {
        if (this.$refs.formLogin.validate()) {
          this.isLoading = true;

          if (process.env.NODE_ENV !== 'development') {
            this.$refs.recaptcha.execute();
          } else {
            await this.login(null);
          }
        }
      },

      async login(user = null) {
        let res;

        if (user?.email) {
          res = await this.$store.dispatch('signUserIn', { ...user, isSocialSignIn: true });
        } else {
          res = await this.$store.dispatch('signUserIn', this.model);
        }

        this.isLoading = false;

        if (['banned', 'suspended'].includes(res?.error) || !this.user || this.isUserBanned) {
          if (this.$route.path === '/') return false;
          return this.$router.push('/');
        }

        if (this.isUserSuspended) {
          EventBus.emit('user-suspended');
          return;
        }

        if (this.$route.path !== '/lobby') {
          return this.$router.push('/lobby');
        }
      },

      async socialSignIn(provider) {
        this.socialIsPending = true;

        try {
          const result = await this.$store.dispatch('socialSignin', provider);

          if (result?.status === 'success' && result.user) {
            if (result.userExists) {
              await this.login(result.user);

              if (result.user.phoneNumber?.verified) {
                await this.$store.dispatch('changeSignupDialogState', false);
                await this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.CREDENTIALS);
              } else {
                await this.$store.dispatch('changeSignupStep', SIGN_UP_STEPS.VERIFY_PHONE);
                await this.$store.dispatch('changeFirstSignInState', false);
              }
            } else {
              this.showSocialError();
            }
          } else {
            throw new Error(result);
          }
        } catch (error) {
          this.showSocialError();
          console.error(`${provider} sign-in error:`, error);
        } finally {
          this.socialIsPending = false;
        }
      },

      async recoverSubmit() {
        if (!this.$refs.formForgotPassword.validate()) {
          return;
        }

        await this.$store.dispatch('sendRecoveryEmail', {
          email: this.model.recoverEmail,
        });
      },

      closeModal() {
        this.model = {
          email: '',
          password: '',
        };

        this.$store.dispatch('changeLoginDialogState', false);
        this.snackbar = false;
      },

      openRegister() {
        this.$store.dispatch('changeLoginDialogState', false);

        setTimeout(() => {
          this.$store.dispatch('changeSignupDialogState', true);
        }, 100);
      },

      toggleForgotPassword(value) {
        this.forgotPassword = value;
        this.model = {
          email: '',
          password: '',
        };

        this.updateKey = !this.updateKey;
      },
    },
  };
</script>

<template>
  <v-dialog
    v-model="value"
    :fullscreen="$vuetify.breakpoint.xs"
    content-class="outlined-modal login-form"
    max-width="720px"
    scrollable
    transition="dialog-top-transition"
    @click:outside="closeModal"
    @keydown.esc="closeModal"
  >
    <template #default>
      <v-card
        v-if="showSignUpForm"
        :max-width="552"
        :width="550"
        class="outlined-modal-card ma-auto d-flex align-center flex-column position-relative"
        data-state="open"
        elevation="0"
        style="border-radius: 20px"
      >
        <v-card-title class="flex-column pa-0">
          <template v-if="!forgotPassword">
            <h2>Login</h2>
            <!--            <div class="mb-6">-->
            <!--              <img src="/images/banners/sign-up-banner.webp?v=1" alt="banner" />-->
            <!--            </div>-->
          </template>
          <template v-else>
            <p class="white--text font-weight-600 font-size-24" data-test-id="forgot-your-password-title">
              Forgot your password?
            </p>
          </template>
        </v-card-title>

        <v-btn class="close-button-white" data-test-id="close-button" icon @click="closeModal">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>

        <template v-if="!forgotPassword">
          <v-form
            class="w-full"
            :key="updateKey"
            ref="formLogin"
            v-model="formValid"
            lazy-validation
            @submit.prevent="loginSubmit"
          >
            <div v-if="hasSocialError" class="social-error" @click="hideSocialError">
              <IconAlert />
              <span>
                Unfortunately there is no account associated with this email address. Please try to log in with
                different Google account or to Sign Up.
              </span>
            </div>
            <button
              type="button"
              :disabled="socialIsPending"
              class="social-button"
              @click="socialSignIn(AUTH_PROVIDERS.GOOGLE)"
            >
              <IconGoogle class="icon" />
              Login with Google
            </button>
            <div class="sign-in-email-text">
              <span>or login with email</span>
            </div>

            <v-card-text class="pa-0">
              <outlined-input
                ref="email"
                label="Email"
                v-model="lowerEmail"
                :rules="[rules.required(), rules.email()]"
                class="formText"
                data-test-id="email-address-input"
                outlined
                persistent-placeholder
                placeholder="your@email.com"
              />
              <outlined-input
                label="Password"
                v-model="model.password"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :rules="[rules.required()]"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                class="formText"
                data-test-id="password-input"
                outlined
              >
                <template #append>
                  <v-btn icon @click="showPassword = !showPassword" tabindex="-1">
                    <v-icon>{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </v-btn>
                </template>
              </outlined-input>
              <div class="d-flex justify-end w-full">
                <a class="gold--text" data-test-id="click-here-forgot-password" @click="toggleForgotPassword(true)">
                  <u class="font-weight-500 font-size-16">Forgot Password?</u>
                </a>
              </div>
              <v-btn :loading="isLoading" class="golden-button w-full my-6" data-test-id="log-in-submit" type="submit">
                Login
              </v-btn>

              <re-captcha ref="recaptcha" @verify="login(null)" />

              <div class="white--text w-full text-center font-size-16">
                Don't have an account?
                <a class="gold--text" data-test-id="click-here-create-account" @click="openRegister">
                  <u class="font-weight-500">Sign Up here</u>
                </a>
              </div>
            </v-card-text>
          </v-form>
        </template>
        <template v-else>
          <v-form
            :key="updateKey"
            ref="formForgotPassword"
            v-model="resetPasswordFormValid"
            lazy-validation
            @submit.prevent="recoverSubmit"
          >
            <p class="white--text font-size-16 text-center" data-test-id="enter-email-text">
              Don't Worry! Just enter your email and we will send you a link to reset your password.
            </p>
            <outlined-input
              label="Email"
              v-model="model.recoverEmail"
              :rules="[rules.email()]"
              class="formText"
              data-test-id="email-input"
              outlined
              persistent-placeholder
              placeholder="Email"
            />
            <div class="mt-6 buttons-container">
              <v-btn class="golden-outlined" @click="toggleForgotPassword(false)">Go back to Login</v-btn>
              <v-btn class="golden-button" data-test-id="reset-password-button" type="submit">
                <span class="pl-2">Send Reset Link</span>
              </v-btn>
            </div>
          </v-form>
        </template>
      </v-card>
    </template>
  </v-dialog>
</template>

<style lang="scss">
  .login-social {
    gap: 10px;
  }

  .buttons-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    @media screen and (max-width: 600px) {
      grid-template-columns: 1fr;
    }
  }

  .formText {
    font-size: 20px !important;
    color: #fff !important;

    i,
    button {
      color: #fff !important;
    }

    input::placeholder {
      /* Chrome, Firefox, Opera, Safari 10.1+ */
      color: #fff !important;
      opacity: 1; /* Firefox */
    }

    input:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: #fff !important;
    }

    input::-ms-input-placeholder {
      /* Microsoft Edge */
      color: #fff !important;
    }
  }

  .formText.v-input--is-focused fieldset {
    border: 1px solid #f8bf1e;
  }

  .formText.v-input--is-focused button {
    color: #f8bf1e !important;
  }

  .formText.v-text-field--outlined.v-input--has-state fieldset {
    border: 2px solid #ff5252;
  }

  .formText.v-input--is-focused i {
    color: #f8bf1e !important;
  }

  .formText.v-text-field--outlined.v-input--has-state i {
    color: #ff5252 !important;
  }

  .btn-login-social {
    border-radius: 40px;
    font-size: 20px !important;
    max-width: 310px;
    width: 100%;
    height: 60px !important;
    text-transform: none;
  }

  .btn-login-social img {
    display: block;
  }

  .btn-login-social.btn-fb {
    background-color: #24599d !important;
    color: #fff;
  }

  .btn-login-social.btn-google {
    border: 1px solid #bbb;
  }

  .btn-login-social .login-btn {
    padding-left: 8px;
  }
</style>

<style lang="scss" scoped>
  .v-card__title h2 {
    color: #fff;
    text-align: center;
    font-family: 'Barlow', sans-serif;
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 24px;
  }

  .social-button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    width: 100%;
    border-radius: 10px;
    background: #fff;
    gap: 10px;
    color: #160526;
    font-size: 18px;
    font-weight: 600;
    line-height: 16px;
    margin-bottom: 24px;

    &:hover {
      filter: brightness(105%);
    }

    &:active {
      filter: brightness(97%);
    }

    &:disabled {
      pointer-events: none;
      filter: grayscale(1);
    }

    svg {
      max-width: 24px;
      max-height: 24px;
    }
  }

  .sign-in-email-text {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 24px;

    span {
      flex-basis: 100%;
      color: #fff;
      text-align: center;
      font-size: 14px;
      font-weight: 400;
      line-height: 12px;
      opacity: 0.7;
    }

    &:before,
    &:after {
      content: '';
      display: inline-block;
      flex-basis: 100%;
      height: 1px;
      background: #fff;
      opacity: 0.3;
    }
  }

  .login-button {
    text-transform: unset;
    min-width: 250px;
    height: 56px;
    font-size: 16px;
    font-weight: 700;

    @media screen and (max-width: 600px) {
      min-width: 150px;
    }
  }

  .social-error {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 24px;
    margin-bottom: 24px;
    cursor: pointer;
    border-radius: 10px;
    background: rgba(255, 0, 0, 0.1);

    svg {
      max-width: 24px;
    }

    span {
      color: #fff;
      font-family: 'Barlow', sans-serif;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
    }
  }

  @media only screen and (max-height: 800px) {
    .joinFields .row {
      max-width: 100%;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    .joinFields form {
      overflow: hidden;
    }
  }
</style>
